(function () {
  'use strict';

  var config = window.nsPerformance || {};
  var scriptPromises = {};
  var interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'mousemove', 'scroll'];

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function loadScript(src, attrs) {
    if (!src) return Promise.resolve();
    if (scriptPromises[src]) return scriptPromises[src];

    scriptPromises[src] = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        resolve(existing);
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      if (attrs) {
        Object.keys(attrs).forEach(function (key) {
          script.setAttribute(key, attrs[key]);
        });
      }
      script.onload = function () { resolve(script); };
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return scriptPromises[src];
  }

  function runOnFirstInteraction(callback, fallbackDelay) {
    var fired = false;
    var run = function () {
      if (fired) return;
      fired = true;
      interactionEvents.forEach(function (eventName) {
        window.removeEventListener(eventName, run, true);
      });
      callback();
    };

    interactionEvents.forEach(function (eventName) {
      window.addEventListener(eventName, run, { once: true, passive: true, capture: true });
    });

    if (fallbackDelay) {
      window.setTimeout(run, fallbackDelay);
    }
  }

  function runOnIdleOrInteraction(callback, fallbackDelay) {
    var ran = false;
    var run = function () {
      if (ran) return;
      ran = true;
      callback();
    };

    runOnFirstInteraction(run, fallbackDelay || 5000);

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: fallbackDelay || 5000 });
    } else {
      window.setTimeout(run, Math.min(fallbackDelay || 5000, 2500));
    }
  }

  function runAfterDelayOrInteraction(callback, delay) {
    var ran = false;
    var run = function () {
      if (ran) return;
      ran = true;
      callback();
    };

    runOnFirstInteraction(run);
    window.setTimeout(function () {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 1500 });
      } else {
        run();
      }
    }, delay || 8000);
  }

  function refreshCartDrawer(source) {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    var drawer = document.querySelector('.js-cart-drawer');
    var cartForm = drawer && drawer.querySelector('cart-form');
    if (cartForm && typeof cartForm.refresh === 'function') cartForm.refresh();
    if (drawer && typeof drawer.open === 'function') drawer.open(source || document.activeElement);
  }

  async function addVariantToCart(variantId, quantity) {
    var response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    });

    if (!response.ok) throw new Error('cart-add');
    return response.json();
  }

  function initHomeRails(root) {
    var scope = root || document;

    scope.querySelectorAll('[data-ns-home-rail-track]').forEach(function (track) {
      if (track.dataset.nsPerformanceRailBound === 'true') return;
      track.dataset.nsPerformanceRailBound = 'true';

      if (track.dataset.nsHomeAutoscroll !== 'true') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (window.matchMedia('(hover: none), (pointer: coarse)').matches && track.dataset.nsHomeAutoscrollMobile !== 'true') return;

      var speed = parseInt(track.dataset.nsHomeScrollSpeed, 10) || 18;
      var visibleOnly = track.dataset.nsRailVisibleOnly !== 'false';
      var paused = false;
      var visible = !visibleOnly;
      var lastTime = null;
      var resumeTimer = null;
      var programmaticScroll = false;

      var maxScroll = function () {
        return Math.max(0, track.scrollWidth - track.clientWidth);
      };

      var pause = function () {
        paused = true;
        lastTime = null;
      };
      var resume = function () {
        paused = false;
        lastTime = null;
      };
      var pauseFor = function (delay) {
        pause();
        window.clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(resume, delay || 1600);
      };

      track.addEventListener('mouseenter', pause);
      track.addEventListener('mouseleave', resume);
      track.addEventListener('focusin', pause);
      track.addEventListener('focusout', resume);
      track.addEventListener('pointerdown', pause, { passive: true });
      track.addEventListener('pointerup', function () { pauseFor(1800); }, { passive: true });
      track.addEventListener('pointercancel', function () { pauseFor(1800); }, { passive: true });
      track.addEventListener('touchstart', pause, { passive: true });
      track.addEventListener('touchend', function () { pauseFor(1800); }, { passive: true });
      track.addEventListener('scroll', function () {
        if (programmaticScroll) return;
        pauseFor(1100);
      }, { passive: true });
      track.addEventListener('wheel', function () { pauseFor(2200); }, { passive: true });
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
          lastTime = null;
        } else {
          pause();
        }
      });

      if (visibleOnly && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          visible = entries.some(function (entry) { return entry.isIntersecting; });
          lastTime = null;
        }, { rootMargin: '180px 0px' });
        observer.observe(track);
      } else {
        visible = true;
      }

      var step = function (time) {
        if (!track.isConnected) return;
        var limit = maxScroll();
        if (limit > 4 && !paused && visible && document.visibilityState === 'visible') {
          if (lastTime !== null) {
            programmaticScroll = true;
            track.scrollLeft += ((time - lastTime) / 1000) * speed;
            if (track.scrollLeft >= limit - 2) track.scrollLeft = 0;
            window.requestAnimationFrame(function () { programmaticScroll = false; });
          }
          lastTime = time;
        } else {
          lastTime = null;
        }
        window.requestAnimationFrame(step);
      };

      window.requestAnimationFrame(step);
    });

    scope.querySelectorAll('[data-ns-home-quick-add]').forEach(function (btn) {
      if (btn.dataset.nsPerformanceQuickBound === 'true') return;
      btn.dataset.nsPerformanceQuickBound = 'true';

      btn.addEventListener('click', async function () {
        var original = btn.textContent;
        btn.textContent = 'Adding...';
        btn.disabled = true;
        btn.style.opacity = '0.72';

        try {
          await addVariantToCart(btn.dataset.nsHomeQuickAdd, 1);
          btn.textContent = 'Added';
          btn.style.opacity = '1';
          refreshCartDrawer(btn);
        } catch (err) {
          btn.textContent = 'Try Again';
          btn.style.opacity = '1';
        }

        window.setTimeout(function () {
          btn.textContent = original;
          btn.disabled = false;
          btn.style.opacity = '';
        }, 1600);
      });
    });
  }

  function initCalendlyWidget(widget) {
    if (!widget || widget.dataset.nsCalendlyLoaded === 'true') return;
    var mode = getCalendlyMode();
    var url = getCalendlyUrl(widget, mode);
    if (!url) return;

    widget.dataset.nsCalendlyLoaded = 'true';
    widget.dataset.nsCalendlyMode = mode;
    widget.dataset.nsCalendlyReady = 'false';
    loadScript('https://assets.calendly.com/assets/external/widget.js').then(function () {
      if (window.Calendly && typeof window.Calendly.initInlineWidget === 'function') {
        window.Calendly.initInlineWidget({ url: url, parentElement: widget });
        watchCalendlyIframe(widget);
      }
    });
  }

  function getCalendlyMode() {
    return document.documentElement.getAttribute('data-ns-night-mode') === 'dark' ? 'dark' : 'light';
  }

  function getCalendlyUrl(widget, mode) {
    return mode === 'dark' && widget.dataset.darkUrl ? widget.dataset.darkUrl : widget.dataset.url;
  }

  function refreshCalendlyTheme() {
    var mode = getCalendlyMode();

    document.querySelectorAll('[data-ns-calendly-widget][data-ns-calendly-loaded="true"]').forEach(function (widget) {
      if (widget.dataset.nsCalendlyMode === mode) return;

      widget.dataset.nsCalendlyLoaded = 'false';
      widget.dataset.nsCalendlyMode = mode;
      widget.dataset.nsCalendlyReady = 'false';
      widget.innerHTML = '';
      initCalendlyWidget(widget);
    });
  }

  function warmCalendlyScript() {
    if (!document.querySelector('[data-ns-calendly-widget]')) return;
    var warm = function () { loadScript('https://assets.calendly.com/assets/external/widget.js'); };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warm, { timeout: 1200 });
    } else {
      window.setTimeout(warm, 500);
    }
  }

  function watchCalendlyIframe(widget) {
    var marked = false;
    var markReady = function () {
      if (marked) return;
      marked = true;
      widget.dataset.nsCalendlyReady = 'true';
    };

    var bindIframe = function () {
      var iframe = widget.querySelector('iframe');
      if (!iframe) return false;
      iframe.addEventListener('load', markReady, { once: true });
      window.setTimeout(markReady, 1800);
      return true;
    };

    if (bindIframe()) return;

    if ('MutationObserver' in window) {
      var observer = new MutationObserver(function () {
        if (bindIframe()) observer.disconnect();
      });
      observer.observe(widget, { childList: true, subtree: true });
      window.setTimeout(function () {
        observer.disconnect();
        markReady();
      }, 4200);
    } else {
      window.setTimeout(markReady, 2400);
    }
  }

  function initCalendly() {
    var widgets = document.querySelectorAll('[data-ns-calendly-widget]');
    if (!widgets.length) return;
    warmCalendlyScript();

    if (!config.delayCalendlyUntilVisible) {
      widgets.forEach(initCalendlyWidget);
      return;
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            initCalendlyWidget(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '900px 0px' });

      widgets.forEach(function (widget) {
        observer.observe(widget);
        widget.addEventListener('click', function () { initCalendlyWidget(widget); }, { once: true });
      });
    } else {
      runOnIdleOrInteraction(function () {
        widgets.forEach(initCalendlyWidget);
      }, 2500);
    }
  }

  function bindCalendlyThemeObserver() {
    if (document.documentElement.dataset.nsCalendlyThemeObserver === 'true') return;
    document.documentElement.dataset.nsCalendlyThemeObserver = 'true';

    var raf = null;
    var queueRefresh = function () {
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(function () {
        raf = null;
        refreshCalendlyTheme();
      });
    };

    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        if (mutations.some(function (mutation) { return mutation.attributeName === 'data-ns-night-mode'; })) {
          queueRefresh();
        }
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-ns-night-mode'] });
    } else {
      document.addEventListener('click', function (event) {
        if (!event.target.closest('[data-ns-night-toggle]')) return;
        window.setTimeout(queueRefresh, 120);
      });
    }
  }

  function initThirdParties() {
    if (config.enableThirdPartyDelay === false) return;

    if (config.whatsapp && config.whatsapp.src && config.whatsapp.delayUntilInteraction) {
      runOnFirstInteraction(function () {
        loadScript(config.whatsapp.src).then(function () {
          if (window.LimeChatWhatsapp) {
            var widget = new window.LimeChatWhatsapp({
              accessToken: config.whatsapp.accessToken,
              phoneNumber: config.whatsapp.phoneNumber
            });
            widget.initializeWidget();
          }
        });
      }, config.whatsapp.fallbackDelay || 7000);
    }
  }

  ready(function () {
    initHomeRails();
    initCalendly();
    bindCalendlyThemeObserver();
    initThirdParties();
  });

  document.addEventListener('shopify:section:load', function (event) {
    initHomeRails(event.target);
    initCalendly();
  });

  window.nsPerformanceInitHomeRails = initHomeRails;
  window.nsPerformanceRefreshCartDrawer = refreshCartDrawer;
  window.nsPerformanceAddVariantToCart = addVariantToCart;
})();
